import { lock, unlock, clearBodyLocks } from "tua-body-scroll-lock";

export interface IModal extends HTMLElement {
  showModal(): void;
  hideModal(): void;
}

export class Modal extends HTMLElement implements IModal {
  public modal: HTMLElement | null;
  private scrollElement: HTMLElement | null;
  private closeModalButtons: HTMLElement[];
  private focusableElements: NodeListOf<HTMLElement> | null;
  private firstFocusableElement: HTMLElement | null;
  private lastFocusableElement: HTMLElement | null;
  private lastFocusedElement: HTMLElement | null = null;
  private modalName: string | null;
  private originalParent: Node | null = null;
  private originalNextSibling: Node | null = null;
  private portal: boolean = false;

  constructor() {
    super();
    this.modal = null;
    this.scrollElement = null;
    this.closeModalButtons = [];
    this.focusableElements = null;
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    this.modalName = null;
  }

  connectedCallback(): void {
    this.getModalName();
    this.cacheDomElements();
    this.setupEventListeners();
    this.checkOpenAttribute();
    this.checkPortalAttribute();
  }

  disconnectedCallback(): void {
    clearBodyLocks();
  }

  private checkOpenAttribute() {
    const open = this.getAttribute("open") === "true";

    if (open) {
      this.showModal();
    }
  }

  private checkPortalAttribute() {
    this.portal = this.getAttribute("portal") === "true";
  }

  private getModalName(): void {
    this.modalName = this.getAttribute("modal-name");
    if (!this.modalName) {
      throw new Error("Modal name attribute is required");
    }
  }

  private cacheDomElements(): void {
    this.modal = this;

    if (!this.modal) {
      throw new Error("Modal element not found");
    }

    this.scrollElement = this.querySelector("[js-modal=scrollElement]");
    this.closeModalButtons = Array.from(
      this.querySelectorAll("[data-close-modal]"),
    );
  }

  private setupEventListeners(): void {
    this.closeModalButtons.forEach((button) => {
      button.addEventListener("click", () => this.hideModal());
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        this.hideModal();
      }
    });

    const overlay = this.querySelector(".modal__overlay");
    if (overlay) {
      overlay.addEventListener("click", this.overlayClickHandler.bind(this));
    }

    document.addEventListener(`modal:show:${this.modalName}`, () =>
      this.showModal(),
    );

    document.addEventListener(`modal:hide:${this.modalName}`, () =>
      this.hideModal(),
    );
  }

  private overlayClickHandler(): void {
    if (!this.hasAttribute("persist-dialog")) {
      this.hideModal();
    }
  }

  public showModal(): void {
    this.lastFocusedElement = document.activeElement as HTMLElement;
    this.modal!.setAttribute("aria-hidden", "false");
    this.classList.add("is-open");

    const header = document.querySelector(
      ".header-background, .header-background-transparent",
    );
    const modalOverlay = this.querySelector(".modal__overlay");
    const isFreshPage = window.location.pathname === "/pages/fresh";

    if (
      modalOverlay &&
      header &&
      this.modalName === "account-overlay" &&
      isFreshPage &&
      header.classList.contains("header-background-transparent")
    ) {
      modalOverlay.classList.remove(
        "modal__overlay--no-margin",
        "modal__overlay--negative-margin",
      );
      modalOverlay.classList.add("modal__overlay--negative-margin");
    }

    if (this.portal) {
      // Store original parent and sibling
      this.originalParent = this.modal!.parentNode;
      this.originalNextSibling = this.modal!.nextSibling;

      // Move modal to the end of the body
      document.body.appendChild(this.modal!);
    }

    this.setupFocusTrap();
    lock(this.scrollElement || this.modal);

    document.dispatchEvent(
      new CustomEvent("modal:show", {
        detail: {
          modalName: this.modalName,
        },
      }),
    );
  }

  public hideModal(restoreFocus = true): void {
    clearBodyLocks();

    this.modal!.setAttribute("aria-hidden", "true");
    this.classList.remove("is-open");

    const modalOverlay = this.querySelector(".modal__overlay");
    if (modalOverlay) {
      modalOverlay.classList.remove(
        "modal__overlay--no-margin",
        "modal__overlay--negative-margin",
      );
    }

    document.removeEventListener("keydown", this.trapFocus);

    if (this.portal && this.originalParent) {
      // Restore modal to its original position
      if (this.originalNextSibling) {
        this.originalParent.insertBefore(this.modal!, this.originalNextSibling);
      } else {
        this.originalParent.appendChild(this.modal!);
      }
    }

    // Restore focus to the element that was focused before the modal was opened
    if (
      this.lastFocusedElement &&
      typeof this.lastFocusedElement.focus === "function" &&
      restoreFocus
    ) {
      this.lastFocusedElement.focus();
    }

    unlock(this.modal);
    document.dispatchEvent(new CustomEvent(`modal:closed:${this.modalName}`));
  }

  private setupFocusTrap(): void {
    this.focusableElements = this.modal!.querySelectorAll(
      'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]',
    );
    if (this.focusableElements.length) {
      this.firstFocusableElement = this.focusableElements[0];
      this.lastFocusableElement =
        this.focusableElements[this.focusableElements.length - 1];
      this.firstFocusableElement.focus();

      document.addEventListener("keydown", this.trapFocus.bind(this));
    }
  }

  private trapFocus(event: KeyboardEvent): void {
    if (event.key === "Tab") {
      if (event.shiftKey) {
        if (document.activeElement === this.firstFocusableElement) {
          this.lastFocusableElement?.focus();
          event.preventDefault();
        }
      } else if (document.activeElement === this.lastFocusableElement) {
        this.firstFocusableElement?.focus();
        event.preventDefault();
      }
    }
  }
}
